import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig, loginRequest, apiConfig } from '../config/authConfig'

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig)

// Initialize MSAL
export const initializeMsal = async () => {
  await msalInstance.initialize()
  await msalInstance.handleRedirectPromise()
}

// Login with redirect
// Page navigates away to AAD; the returned Promise resolves only on failure.
export const loginWithRedirect = async (extraOptions = {}) => {
  try {
    await msalInstance.loginRedirect({ ...loginRequest, ...extraOptions })
  } catch (error) {
    console.error('Login failed:', error)
    throw error
  }
}

// Logout (redirect flow)
// Performs full AAD logout and redirects browser to postLogoutRedirectUri.
export const logout = async () => {
  try {
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
    })
  } catch (error) {
    console.error('Logout failed:', error)
    throw error
  }
}

// Get access token (silent first; falls back to redirect if interaction needed)
export const getAccessToken = async () => {
  const accounts = msalInstance.getAllAccounts()

  if (accounts.length === 0) {
    throw new Error('No accounts found')
  }

  const request = {
    scopes: apiConfig.scopes,
    account: msalInstance.getActiveAccount() || accounts[0],
  }

  try {
    // Try to acquire token silently
    const response = await msalInstance.acquireTokenSilent(request)
    return response.accessToken
  } catch (error) {
    console.warn('Silent token acquisition failed; falling back to redirect')
    try {
      // Page navigates away to AAD; this call will not return on success.
      await msalInstance.acquireTokenRedirect(request)
      throw error
    } catch (redirectError) {
      console.error('Token acquisition failed:', redirectError)
      throw redirectError
    }
  }
}

// Get current user account
export const getCurrentAccount = () => {
  return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null
}

// Check if user is authenticated
export const isAuthenticated = () => {
  const accounts = msalInstance.getAllAccounts()
  return accounts.length > 0
}

// Get user info
export const getUserInfo = () => {
  const account = getCurrentAccount()
  if (!account) {
    return null
  }

  return {
    username: account.username,
    name: account.name,
    email: account.username,
    id: account.homeAccountId,
  }
}
